import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { LogoMark } from "@/components/site/Logo";
import { EASE } from "@/components/site/motion";
import { useCart } from "@/context/CartContext";
import { CreateAccountCta } from "@/components/account/CreateAccountCta";
import { api } from "@/lib/api";

const COPY = {
  checking: { icon: Loader2, title: "Sto verificando il pagamento…", text: "Un attimo, sto controllando che sia andato tutto a buon fine." },
  paid: { icon: CheckCircle2, title: "Pagamento confermato.", text: "Grazie per aver scelto sfushop! Ti ho inviato un'email con il riepilogo dell'ordine. Il tuo pezzo parte entro 48 ore." },
  expired: { icon: XCircle, title: "Sessione scaduta.", text: "Il pagamento non è andato a buon fine. Il tuo carrello è ancora lì che ti aspetta." },
  timeout: { icon: Loader2, title: "Verifica in corso.", text: "Non riesco ancora a confermare il pagamento. Se l'addebito è andato a buon fine riceverai un'email di conferma a breve." },
  cancel: { icon: XCircle, title: "Pagamento annullato.", text: "Nessun addebito. Il tuo carrello è ancora qui, quando vuoi." },
};

const usePaymentState = (outcome, sessionId, onPaid) => {
  const [state, setState] = useState(outcome === "cancel" ? "cancel" : sessionId ? "checking" : "timeout");

  useEffect(() => {
    if (outcome !== "success" || !sessionId) return undefined;
    let tries = 0;
    let timer;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!alive) return;
        if (data.payment_status === "paid") {
          setState("paid");
          onPaid();
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setState("expired");
          return;
        }
      } catch {
        /* retry on next tick */
      }
      tries += 1;
      if (tries >= 15) setState("timeout");
      else timer = setTimeout(poll, 2000);
    };
    poll();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome, sessionId]);

  return state;
};

export default function PaymentResult({ outcome }) {
  const [params] = useSearchParams();
  const { clear } = useCart();
  const state = usePaymentState(outcome, params.get("session_id"), clear);
  const { icon: Icon, title, text } = COPY[state];

  return (
    <section data-testid="payment-result" data-state={state} className="flex min-h-screen flex-col px-5 py-8 sm:px-8 lg:px-12">
      <Link to="/" className="flex items-center gap-2.5" data-testid="payment-logo-link">
        <LogoMark className="h-8 w-8" />
        <span className="font-display text-sm font-black tracking-[0.2em]">SFUMO</span>
      </Link>
      <motion.div
        key={state}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
        className="my-auto max-w-3xl py-20"
      >
        <Icon className={`h-14 w-14 ${state === "paid" ? "text-acid" : state === "checking" || state === "timeout" ? "animate-spin text-smoke" : "text-red-400"}`} />
        <h1 data-testid="payment-result-title" className="mt-8 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">{title}</h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-bone/70 sm:text-lg">{text}</p>
        {state === "paid" && <div className="mt-8 max-w-md"><CreateAccountCta testid="payment-account-cta" /></div>}
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/" data-testid="payment-back-home" className="inline-flex h-12 items-center rounded-full bg-acid px-6 font-mono text-xs uppercase tracking-[0.18em] text-ink transition-transform hover:-translate-y-0.5">
            Torna al sito
          </Link>
          <Link to="/#sfushop" data-testid="payment-back-shop" className="inline-flex h-12 items-center rounded-full border border-bone/30 px-6 font-mono text-xs uppercase tracking-[0.18em] transition-colors hover:border-acid hover:text-acid">
            Continua lo shopping
          </Link>
        </div>
      </motion.div>
    </section>
  );
}
